import type { HTMLAttributes, ReactNode } from "react";
import { cn } from "@/lib/utils";

/**
 * Coolicons — 442-glyph webfont icon set shipped in public/fonts/coolicons.
 *
 * Why a webfont wrapper: the upstream CSS used a naked `i {}` selector
 * that would hijack every italic tag in the app. We instead scope it
 * to the `.ci-*` class family via `coolicons.css`, and expose this typed
 * wrapper so you get a uniform `<CoolIcon name="Add_Plus" />` call site
 * that matches the ergonomics of `lucide-react`.
 *
 * Icon name reference: `Webfont/coolicons.html` in the source bundle, or
 * the `.ci-*` class list in `src/coolicons.css`.
 */
export interface CoolIconProps
  extends Omit<HTMLAttributes<HTMLSpanElement>, "children"> {
  /**
   * Coolicon glyph name, matching the `ci-<Name>` class — e.g. "Add_Plus",
   * "Arrow_Circle_Right", "Copy", "Check".
   */
  name: string;
  /**
   * Pixel size. Renders via `font-size`. Accepts numbers (interpreted as
   * px) or CSS lengths. Defaults to `1em` so it inherits surrounding text.
   */
  size?: number | string;
  /** Accessible label. If omitted, icon is marked decorative via aria-hidden. */
  label?: string;
}

function toLength(v: number | string | undefined): string | undefined {
  if (v === undefined) return undefined;
  return typeof v === "number" ? `${v}px` : v;
}

export function CoolIcon({
  name,
  size,
  label,
  className,
  style,
  ...rest
}: CoolIconProps): ReactNode {
  const length = toLength(size);
  return (
    <span
      className={cn(`ci-${name}`, "inline-block leading-none", className)}
      style={{
        fontSize: length,
        width: length,
        height: length,
        ...style,
      }}
      role={label ? "img" : undefined}
      aria-label={label}
      aria-hidden={label ? undefined : true}
      {...rest}
    />
  );
}

export default CoolIcon;
